"use client";

import React, { useMemo } from "react";
import {
  DynamicFlow,
  GlobalContextProvider,
} from "@highereducation/supersonic-ui";
import type {
  MonarchBase,
  DynamicFlowBaseProps,
  SiteConfig,
} from "@highereducation/supersonic-ui";
import { getEnv } from "@/env";
import { FeatureFlagComponentResolverClient } from "@/lib/feature-flags/FeatureFlagResolverClient";
import MonarchResolver from "@/monarch/MonarchResolver";
import { MonarchDataConsumer } from "@/monarch/MonarchDataConsumer";
import { DynamicExperienceFlowResult } from "@/monarch/rules/dynamicExperienceFlow/types";
import getUserContextFromClient from "@/lib/user-context/getUserContextFromClient";
import { getWebContext } from "@/utils/tagular/helpers";
import { useMultitenantConfig } from "@/contexts/MultitenantConfigContext";

/**
 * Wrapper component that uses MonarchResolver and MonarchDataConsumer
 * to provide GlobalContextProvider for DynamicFlow
 */
export default function DynamicFlowWrapper(props: DynamicFlowBaseProps) {
  // Get EDX-specific user context from cookies (country, region, mobile)
  const additionalUserContext = useMemo(() => getUserContextFromClient(), []);
  const additionalWebContext = useMemo(() => getWebContext(), []);
  const multitenantConfig = useMultitenantConfig();

  return (
    <MonarchResolver<DynamicExperienceFlowResult>
      experimentKey="useDynamicExperienceFlow"
      payload={{
        placement: props.placement || "dashboard",
        forcedFlowId: props.forcedFlowId,
      }}
      variants={{
        control: null,
        test: (
          <MonarchDataConsumer<DynamicExperienceFlowResult>
            experimentKey="useDynamicExperienceFlow"
            render={(testResult) => {
              const value = testResult?.data?.data;

              // Transform the monarch rule response into the MonarchBase structure
              const monarchBase: MonarchBase = {
                placedTopAsset: null,
                placedHeroAsset: null,
                placedBottomAsset: null,
                isMonarchLoading: false, // Data is already loaded by MonarchResolver
                joQDF: {
                  data: {
                    PersonalizedAssetType: "default",
                    PersonalizedAssetData: {},
                  },
                },
                dynamicExperience: value?.flowId
                  ? {
                      [props.placement || "dashboard"]: value.flowId,
                    }
                  : undefined,
              };

              const globalProps = {
                monarch: monarchBase,
                domain: "edx.org",
                template: "authx",
                siteConfig: {
                  tagularSourceKey: getEnv("NEXT_PUBLIC_AUTHX_TENANT_ID"),
                  configs: {
                    multitenant: {
                      ...multitenantConfig,
                    },
                  } as unknown as SiteConfig["configs"],
                },
              };

              // Allow props override from monarch data if needed
              const mergedProps = {
                ...props,
                ...(value?.placementData || {}),
                forcedFlowId: value?.flowId ?? props.forcedFlowId,
                additionalUserContext,
                additionalWebContext,
              };

              return (
                <div className="ss-dynamic-experience-wrapper">
                  <GlobalContextProvider
                    globalProps={globalProps}
                    monarchBase={monarchBase}
                    monarchTrafficFlow={{
                      err: null,
                      result: { source: {} },
                      isLoading: false,
                      rules: {},
                      slots: {},
                      preampPlacements: {},
                      preampAssets: [],
                    }}
                  >
                    <DynamicFlow {...(mergedProps as any)} />
                  </GlobalContextProvider>
                </div>
              );
            }}
          />
        ),
      }}
      Loader={null} // Or provide a loader if desired
      fallbackVariant="test"
    />
  );
}
